import React, { useRef, useState, useEffect } from 'react';

const DedupControl = ({ 
  enableDedup, 
  setEnableDedup, 
  dedupLevel, 
  setDedupLevel,
  dedupData,
  loading,
  setDedupData,
  setDedupMarkers,
  setSelectedDedupGroup,
  setShowDedupModal,
  setDedupRectangles,
  dedupRectangles,
  setDedupRectanglesKey,
  dedupRectanglesKey,
  addressSimilarityThreshold,
  setAddressSimilarityThreshold
}) => {
  // Local state for immediate UI update while dragging
  const [localThreshold, setLocalThreshold] = useState(addressSimilarityThreshold);
  const thresholdDebounceRef = useRef(null);
  
  // Sync local state when prop changes from outside
  useEffect(() => {
    setLocalThreshold(addressSimilarityThreshold);
  }, [addressSimilarityThreshold]);
  
  const handleLevelChange = (event) => {
    const newLevel = parseInt(event.target.value);
    
    // Set the new level - fetchDedupData will handle clearing existing data
    setDedupLevel(newLevel);
  };
  
  const handleThresholdChange = (event) => {
    const rawValue = parseInt(event.target.value);
    
    // Snap to nearest 10% increment (step="10" handles this, but ensure it's rounded)
    const newThreshold = Math.round(rawValue / 10) * 10;
    
    // Update local state immediately for responsive UI
    setLocalThreshold(newThreshold);
    
    // Clear any existing debounce timer
    if (thresholdDebounceRef.current) {
      clearTimeout(thresholdDebounceRef.current);
    }
    
    // Debounce the actual state update (triggers API call)
    // Wait 800ms after user stops dragging before making the request
    thresholdDebounceRef.current = setTimeout(() => {
      setAddressSimilarityThreshold(newThreshold);
    }, 800);
  };
  
  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (thresholdDebounceRef.current) {
        clearTimeout(thresholdDebounceRef.current);
      }
    };
  }, []);

  return (
    <div style={{
      backgroundColor: '#f8f9fa',
      border: '1px solid #dee2e6',
      borderRadius: '8px',
      padding: '16px',
      marginBottom: '16px',
      boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
    }}>
      <h3 style={{
        margin: '0 0 12px 0',
        fontSize: '16px',
        fontWeight: '600',
        color: '#495057'
      }}>
        De-Duplication Settings
      </h3>
      
      {/* Enable/Disable Checkbox */}
      <div style={{ marginBottom: '16px' }}>
        <label style={{
          display: 'flex',
          alignItems: 'center',
          cursor: 'pointer',
          fontSize: '14px',
          color: '#495057'
        }}>
          <input
            type="checkbox"
            checked={enableDedup}
            onChange={(e) => setEnableDedup(e.target.checked)}
            style={{
              marginRight: '8px',
              transform: 'scale(1.1)'
            }}
          />
          Enable De-Dup Check
        </label>
        <p style={{
          margin: '4px 0 0 24px',
          fontSize: '12px',
          color: '#6c757d',
          fontStyle: 'italic'
        }}>
          Show aggregated search markers based on DigiPin levels
        </p>
      </div>

      {/* Level Slider */}
      {enableDedup && (
        <div>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '8px'
          }}>
            <label style={{
              fontSize: '14px',
              color: '#495057',
              fontWeight: '500'
            }}>
              DigiPin Level: {dedupLevel}
            </label>
            <span style={{
              fontSize: '12px',
              color: '#6c757d',
              backgroundColor: '#e9ecef',
              padding: '2px 6px',
              borderRadius: '4px'
            }}>
              {dedupLevel === 10 ? '4m × 4m' : 
               dedupLevel === 9 ? '~12m × 12m' :
               dedupLevel === 8 ? '~36m × 36m' :
               dedupLevel === 7 ? '~108m × 108m' :
               dedupLevel === 6 ? '~324m × 324m' :
               dedupLevel === 5 ? '~972m × 972m' :
               dedupLevel === 4 ? '~2.9km × 2.9km' :
               dedupLevel === 3 ? '~8.7km × 8.7km' :
               dedupLevel === 2 ? '~26km × 26km' :
               '~78km × 78km'}
            </span>
          </div>
          
          <input
            type="range"
            min="1"
            max="10"
            value={dedupLevel}
            onChange={handleLevelChange}
            style={{
              width: '100%',
              height: '6px',
              borderRadius: '3px',
              background: '#dee2e6',
              outline: 'none',
              cursor: 'pointer'
            }}
          />
          
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            fontSize: '11px',
            color: '#6c757d',
            marginTop: '4px'
          }}>
            <span>Level 1</span>
            <span>Level 5</span>
            <span>Level 10</span>
          </div>
        </div>
      )}

      {/* Address Similarity Threshold Slider - Fixed 10% increments */}
      {enableDedup && (
        <div style={{ marginTop: '16px' }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '8px'
          }}>
            <label style={{
              fontSize: '14px',
              color: '#495057',
              fontWeight: '500'
            }}>
              Address Similarity Threshold: {localThreshold}%
            </label>
          </div>
          
          <input
            type="range"
            min="0"
            max="100"
            step="10"
            value={localThreshold}
            onChange={handleThresholdChange}
            style={{
              width: '100%',
              height: '6px',
              borderRadius: '3px',
              background: '#dee2e6',
              outline: 'none',
              cursor: 'pointer'
            }}
          />
          
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            fontSize: '11px',
            color: '#6c757d',
            marginTop: '4px'
          }}>
            <span>0%</span>
            <span>50%</span>
            <span>100%</span>
          </div>
          
          <p style={{
            margin: '4px 0 0 0',
            fontSize: '12px',
            color: '#6c757d',
            fontStyle: 'italic'
          }}>
            {localThreshold > 0 
              ? `Only count searches with addresses similarity ≥ ${localThreshold}%`
              : 'No address filtering (count all searches within DigiPin area)'}
          </p>
        </div>
      )}

                {/* Status Information */}
          {enableDedup && (
            <div style={{
              marginTop: '12px',
              padding: '8px 12px',
              backgroundColor: '#e7f3ff',
              border: '1px solid #b3d9ff',
              borderRadius: '4px',
              fontSize: '12px',
              color: '#0056b3'
            }}>
              {loading ? (
                <span>Loading de-duplication data...</span>
              ) : dedupData ? (
                <div>
                  <div>
                    Found {dedupData.total_groups} groups with {dedupData.total_searches} total searches
                  </div>
                  {dedupData.reference_digipin && (
                    <div style={{ marginTop: '4px', fontSize: '11px', opacity: 0.8 }}>
                      Matching: {dedupData.reference_digipin} (Level {dedupLevel})
                    </div>
                  )}
                </div>
              ) : (
                                 <span>Search for a location first to see de-duplication data</span>
               )}
             </div>
           )}


     </div>
   );
 };

export default DedupControl;
